import React, { useEffect, useMemo, useState } from "react";
import { Alert, Modal, Radio, Space, Table, Tag, Typography } from "antd";
import * as dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { Num } from "components/shared-components/purchase";
import { CURRENCY } from "constants/DeliveryConstants";
import { DATE_FORMAT_DD_MM_YYYY, DATE_FORMAT_DD_MM_YYYY_HH_mm } from "constants/DateConstant";
import { formatPrice } from "functions/formatters";
import { convertQuote, getQuoteApiError } from "functions/api/quotes";
import {
  LINE_KIND,
  QUOTE_DISPLAY_STATUS,
  QUOTE_SEND_STATE,
  countByKind,
  getLineKind,
  getQuoteDisplayStatus,
  getQuoteSendState,
  splitQuoteLinesForOrder,
} from "functions/quotes";

const { Text } = Typography;

/**
 * "Omzetten naar bestelling": de klant heeft gebeld dat hij akkoord is.
 *
 * Eerst opnieuw verdelen over voorraad: de offerte kan weken oud zijn. Wat er nu uit voorraad
 * kan, wat pre-order wordt en wat tijdelijk niet leverbaar is, staat hier vóór je bevestigt — en
 * komt zo ook op de bestelling (dezelfde vlaggen als bij een webshopbestelling, dus de bekende
 * tag "Tijdelijk niet leverbaar" in de orderlijst). Is het gemengd, dan kies je hier of het in
 * delen geleverd wordt, net als in de checkout.
 *
 * Prijzen en aantallen veranderen niet: dat is wat de klant geaccepteerd heeft.
 */
const ConvertQuoteModal = ({ quote, open, onClose, onConverted }) => {
  const { t } = useTranslation();
  const catalogue = useSelector((state) => state.firestoreReducer.ordered.products);
  const [partialDelivery, setPartialDelivery] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [error, setError] = useState(null);

  // Eén keer per openen berekenen: verandert de voorraad terwijl de dialoog openstaat, dan zie je
  // niet ongemerkt iets anders dan wat je bevestigt.
  const split = useMemo(
    () => (open && quote ? splitQuoteLinesForOrder(quote.products || [], catalogue || []) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open, quote?.id, quote?.version],
  );

  useEffect(() => {
    if (!open) return;
    setError(null);
    setPartialDelivery(!!quote?.delivery?.partialDelivery);
  }, [open, quote]);

  if (!quote || !split) return null;

  const counts = countByKind(split.lines);
  const displayStatus = getQuoteDisplayStatus(quote);
  const sendState = getQuoteSendState(quote);
  const missingNames = split.missingProductIds.map(
    (id) => (quote.products || []).find((line) => line.productId === id)?.name || id,
  );

  const handleConvert = async () => {
    setIsConverting(true);
    setError(null);
    try {
      const result = await convertQuote(quote.id, {
        products: split.lines,
        partialDelivery: split.isMixed ? partialDelivery : false,
        expectedShippingDate: split.expectedShippingDate,
      });
      onConverted?.(result);
      onClose();
    } catch (err) {
      console.error("Offerte omzetten mislukt:", err);
      setError(getQuoteApiError(err, t("quote.convert.failed")));
    } finally {
      setIsConverting(false);
    }
  };

  const kindTag = (line) => {
    switch (getLineKind(line)) {
      case LINE_KIND.PRE_ORDER:
        return (
          <Tag color="blue">
            {line.preOrder?.availableAt
              ? t("quote.kind.pre_order_from", { date: line.preOrder.availableAt })
              : t("quote.kind.pre_order")}
          </Tag>
        );
      case LINE_KIND.UNAVAILABLE:
        return <Tag color="gold">{t("unavailable_item")}</Tag>;
      default:
        return <Tag color="green">{t("quote.kind.now")}</Tag>;
    }
  };

  const columns = [
    {
      title: t("product"),
      dataIndex: "name",
      render: (value, line) => (
        <>
          <div>{value}</div>
          {line.code ? (
            <Num muted size={12}>
              {line.code}
            </Num>
          ) : null}
        </>
      ),
    },
    { title: t("quote.kind.title"), key: "kind", width: 190, render: (_, line) => kindTag(line) },
    {
      title: t("quantity"),
      dataIndex: "quantity",
      width: 80,
      align: "right",
      render: (value) => <Num align="right">{value}</Num>,
    },
    {
      title: t("quote.line_total"),
      key: "total",
      width: 110,
      align: "right",
      render: (_, line) => <Num align="right">{CURRENCY + formatPrice(line.paymentPrice * line.quantity)}</Num>,
    },
  ];

  return (
    <Modal
      open={open}
      width={720}
      title={t("quote.convert.title", { number: quote.quoteNumber })}
      okText={t("quote.convert.ok")}
      cancelText={t("cancel")}
      confirmLoading={isConverting}
      onOk={handleConvert}
      onCancel={onClose}
      destroyOnClose
    >
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        {displayStatus === QUOTE_DISPLAY_STATUS.EXPIRED ? (
          <Alert
            type="warning"
            showIcon
            message={t("quote.convert.expired", {
              date: dayjs.unix(quote.validUntil).format(DATE_FORMAT_DD_MM_YYYY),
            })}
          />
        ) : null}
        {sendState.state === QUOTE_SEND_STATE.CHANGED ? (
          <Alert
            type="warning"
            showIcon
            message={t("quote.convert.changed_since_sent", {
              sentVersion: sendState.version,
              version: quote.version || 1,
              date: dayjs.unix(sendState.at).format(DATE_FORMAT_DD_MM_YYYY_HH_mm),
            })}
          />
        ) : null}
        {sendState.state === QUOTE_SEND_STATE.NEVER || sendState.state === QUOTE_SEND_STATE.FAILED ? (
          <Alert type="info" showIcon message={t("quote.convert.never_sent")} />
        ) : null}
        {missingNames.length ? (
          <Alert
            type="warning"
            showIcon
            message={t("quote.convert.missing_products", { names: missingNames.join(", ") })}
          />
        ) : null}

        <div>
          <Text strong>{t("quote.convert.stock_heading")}</Text>
          <div style={{ fontSize: 13, color: "rgba(0, 0, 0, 0.55)", marginTop: 2 }}>
            {t("quote.convert.stock_summary", {
              now: counts[LINE_KIND.NOW],
              preOrder: counts[LINE_KIND.PRE_ORDER],
              unavailable: counts[LINE_KIND.UNAVAILABLE],
            })}
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={split.lines.map((line, index) => ({ ...line, rowKey: index }))}
          rowKey="rowKey"
          pagination={false}
          size="small"
          scroll={{ x: 560, y: 320 }}
        />

        {split.isMixed ? (
          <div>
            <Text strong>{t("quote.convert.delivery_heading")}</Text>
            <Radio.Group
              style={{ display: "block", marginTop: 6 }}
              value={partialDelivery}
              onChange={(event) => setPartialDelivery(event.target.value)}
            >
              <Space direction="vertical">
                <Radio value={false}>{t("quote.convert.delivery_at_once")}</Radio>
                <Radio value={true}>
                  {split.expectedShippingDate
                    ? t("quote.convert.delivery_partial_with_date", { date: split.expectedShippingDate })
                    : t("quote.convert.delivery_partial")}
                </Radio>
              </Space>
            </Radio.Group>
          </div>
        ) : null}

        <Text type="secondary" style={{ fontSize: 12 }}>
          {t("quote.convert.explainer", { email: quote.customer?.email || "-" })}
        </Text>

        {error ? <Alert type="error" showIcon message={error} /> : null}
      </Space>
    </Modal>
  );
};

export default ConvertQuoteModal;
